// Helpers for the Top Features (Feature Usage) page. Pure functions, no React.

export const DEFAULT_FEATURE_FILTERS = {
    plan: 'all',
    plugin_version: '',
    wp_version: '',
    locale: '',
    window: 30,
    since: '',
    only_production: true,
};

export const PLAN_OPTIONS = [
    { value: 'all', label: 'All sites' },
    { value: 'free', label: 'Free' },
    { value: 'pro', label: 'Pro' },
];

export const WINDOW_OPTIONS = [
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
    { value: 90, label: 'Last 90 days' },
    { value: 'custom', label: 'Custom date' },
];

/** Today's date in the browser's time zone, as YYYY-MM-DD (the value format of <input type="date">). */
export function todayIso() {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** "2026-09-01" -> "Sep 1, 2026". */
export function formatIsoDate(iso) {
    const date = new Date(`${iso}T00:00:00`);
    if (!iso || Number.isNaN(date.getTime())) return iso || '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Custom date chosen but no start date picked yet: nothing to request. */
export function isCustomRangeIncomplete(filters) {
    return filters.window === 'custom' && !filters.since;
}

export const CHART_LIMIT = 10;

/**
 * Query parameters for GET /api/v1/dashboard/{productId}/feature-usage.
 * Search and order are applied in the browser, so typing never triggers a request.
 */
export function buildFeatureUsageParams(filters) {
    const params = new URLSearchParams();
    params.set('plan', filters.plan || 'all');
    if (filters.window === 'custom' && filters.since) {
        params.set('since', filters.since);
    } else {
        params.set('window', String(Number(filters.window) || DEFAULT_FEATURE_FILTERS.window));
    }
    params.set('only_production', filters.only_production ? '1' : '0');
    if (filters.plugin_version) params.set('plugin_version', filters.plugin_version);
    if (filters.wp_version) params.set('wp_version', filters.wp_version);
    if (filters.locale) params.set('locale', filters.locale);
    return params;
}

/*
 * Page state in the URL (the source of truth), e.g.
 *   #/stores/1/plugins/1/top-features?plan=pro&window=custom&since=2025-10-01&live=0&q=track&order=least
 * Only values that differ from the defaults are written, in a fixed order, so equal views
 * give equal URLs. Anything invalid in a pasted or hand-edited URL falls back to the default.
 */
const VERSION_RE = /^\d+(\.\d+)?$/;
const LOCALE_RE = /^[A-Za-z]{2,3}(_[A-Za-z0-9]{2,8}){0,2}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const SEARCH_MAX = 100;

/** URL query (string or URLSearchParams) -> { filters, search, order }. */
export function parseFeatureUsageQuery(query) {
    const p = query instanceof URLSearchParams ? query : new URLSearchParams(query || '');
    const get = (name) => (p.get(name) || '').trim();
    const filters = { ...DEFAULT_FEATURE_FILTERS };

    if (PLAN_OPTIONS.some((o) => o.value === get('plan'))) filters.plan = get('plan');
    if (VERSION_RE.test(get('plugin_version'))) filters.plugin_version = get('plugin_version');
    if (VERSION_RE.test(get('wp_version'))) filters.wp_version = get('wp_version');
    if (LOCALE_RE.test(get('locale'))) filters.locale = get('locale');

    const since = DATE_RE.test(get('since')) && get('since') <= todayIso() ? get('since') : '';
    const windowValue = get('window');
    if (windowValue === 'custom' || (since && !windowValue)) {
        filters.window = 'custom';
        filters.since = since;
    } else if (WINDOW_OPTIONS.some((o) => String(o.value) === windowValue)) {
        filters.window = Number(windowValue);
    }

    if (get('live') === '0') filters.only_production = false;

    return {
        filters,
        search: (p.get('q') || '').slice(0, SEARCH_MAX),
        order: get('order') === 'least' ? 'least' : 'most',
    };
}

/** { filters, search, order } -> canonical query string without "?" ('' for the default view). */
export function featureUsageQueryString({ filters, search = '', order = 'most' }) {
    const f = { ...DEFAULT_FEATURE_FILTERS, ...filters };
    const p = new URLSearchParams();
    if (f.plan !== DEFAULT_FEATURE_FILTERS.plan) p.set('plan', f.plan);
    if (f.plugin_version) p.set('plugin_version', f.plugin_version);
    if (f.wp_version) p.set('wp_version', f.wp_version);
    if (f.locale) p.set('locale', f.locale);
    if (f.window === 'custom') {
        p.set('window', 'custom');
        if (f.since) p.set('since', f.since);
    } else if (Number(f.window) !== DEFAULT_FEATURE_FILTERS.window) {
        p.set('window', String(f.window));
    }
    if (!f.only_production) p.set('live', '0');
    if (search) p.set('q', search.slice(0, SEARCH_MAX));
    if (order === 'least') p.set('order', 'least');
    return p.toString();
}

/*
 * Last view per product for this browser tab (sessionStorage, like table pagination), so coming
 * back through the sidebar link (which has no query) restores it. Storage can be unavailable
 * (private mode, blocked site data): every access is guarded and simply does nothing then.
 */
const VIEW_STORAGE_PREFIX = 'telemetry.topFeatures';

export function readSavedFeatureView(productId) {
    try {
        return window.sessionStorage.getItem(`${VIEW_STORAGE_PREFIX}.${productId}`) || '';
    } catch {
        return '';
    }
}

export function saveFeatureView(productId, query) {
    try {
        const key = `${VIEW_STORAGE_PREFIX}.${productId}`;
        if (query) window.sessionStorage.setItem(key, query);
        else window.sessionStorage.removeItem(key);
    } catch {
        // Storage unavailable: the URL still holds the view.
    }
}

/** Features filtered by search and sorted for display. Rank stays the API's usage rank. */
export function filterAndSortFeatures(features, search, order) {
    const list = Array.isArray(features) ? features : [];
    const needle = (search || '').trim().toLowerCase();
    const filtered = needle
        ? list.filter((f) => String(f.key).toLowerCase().includes(needle)
            || String(f.label).toLowerCase().includes(needle))
        : list.slice();

    filtered.sort((a, b) => (b.usage_pct - a.usage_pct) || String(a.key).localeCompare(String(b.key)));
    return order === 'least' ? filtered.reverse() : filtered;
}

/** Filters that differ from the defaults, as removable chips. */
export function activeFilterChips(filters, localeLabel = (code) => code) {
    const chips = [];
    if (filters.plan !== DEFAULT_FEATURE_FILTERS.plan) {
        chips.push({ key: 'plan', label: filters.plan === 'pro' ? 'Pro sites' : 'Free sites' });
    }
    if (filters.plugin_version) chips.push({ key: 'plugin_version', label: `Plugin ${filters.plugin_version}.x` });
    if (filters.wp_version) chips.push({ key: 'wp_version', label: `WordPress ${filters.wp_version}` });
    if (filters.locale) chips.push({ key: 'locale', label: localeLabel(filters.locale) });
    if (filters.window === 'custom') {
        if (filters.since) chips.push({ key: 'window', label: `Since ${formatIsoDate(filters.since)}` });
    } else if (Number(filters.window) !== DEFAULT_FEATURE_FILTERS.window) {
        chips.push({ key: 'window', label: `Last ${filters.window} days` });
    }
    if (!filters.only_production) chips.push({ key: 'only_production', label: 'Including test sites' });
    return chips;
}

/** Flat rows for CSV export. */
export function featureCsvRows(features) {
    return (features || []).map((f) => ({
        rank: f.rank,
        label: f.label,
        key: f.key,
        source: f.source,
        sites_using: f.sites_using,
        usage_pct: f.usage_pct,
        top_value: f.top_values?.[0]?.value ?? '',
        top_value_pct: f.top_values?.[0]?.pct ?? '',
        default_on: f.default_on ? 'yes' : 'no',
    }));
}

export const FEATURE_CSV_COLUMNS = [
    { key: 'rank', label: 'Rank' },
    { key: 'label', label: 'Setting' },
    { key: 'key', label: 'Key' },
    { key: 'source', label: 'Source' },
    { key: 'sites_using', label: 'Sites using' },
    { key: 'usage_pct', label: 'Usage %' },
    { key: 'top_value', label: 'Most common value' },
    { key: 'top_value_pct', label: 'Most common value %' },
    { key: 'default_on', label: 'On by default' },
];

/** Count for display; estimated counts (sampled products) get a leading "≈". */
export function formatCount(value, estimated) {
    return `${estimated ? '≈ ' : ''}${formatNumber(value)}`;
}

export function formatNumber(value) {
    return Number(value || 0).toLocaleString('en-US');
}
